import React, { useState, useEffect } from "react";
import TickerModal from "./TickerModal";
import HoverModal from "./hover-modal/HoverModal";
import AlertModal from "./AlertModal";
import {
  usCurrency,
  noDecimals,
  usdCurrency,
} from "../../../_shared/helpers/formatting";
import Tooltip from "../../_shared/components/Tooltip";
import { API } from "aws-amplify";
import { useQueryClient } from "@tanstack/react-query";
import { useZustandStore } from "_shared/utils/zustandStore";
import MoneylineColumn from "./MoneylineColumn";

const isWeekend = [0, 6].includes(new Date().getDay());

function FormatCell({
  cell,
  i,
  index,
  data,
  setRefreshTable,
  page,
  kiss,
  longbowPanel,
  planInterval,
  setPlanInterval,
  closingDate,
  updatedDate,
  signalAlertsSubscription,
  setSignalAlertsSubscription,
  alertsInterval,
  includeSignal,
  setDisableDeleteContextMenu,
  compactMode,
  bullseye,
}) {
  const queryClient = useQueryClient();
  const [beforeSplit, setBeforeSplit] = useState("");
  const [afterSplit, setAfterSplit] = useState("");
  const afterSplitDt = [];
  const beforeSplitDt = [];

  const moneylineDays = useZustandStore((state) => state.moneylineDays);

  const momoDict = {
    NEUTRAL: "hold",
    BEARISH: "sell",
    BULLISH: "buy",
  };

  //Get period to show Stock Split indicator.
  useEffect(() => {
    const newDate = new Date();
    const year = newDate.getFullYear();
    const month = newDate.getMonth() + 1;
    const day = newDate.getDate();

    for (let i = 0; i <= 3; i++) {
      beforeSplitDt.push(
        `${year}-${month < 10 ? "0" + month : month}-${day + i}`,
      );
      afterSplitDt.push(
        `${year}-${month < 10 ? "0" + month : month}-${day - i - 1}`,
      );
    }
    setBeforeSplit(
      beforeSplitDt
        .filter((date) => date === data[i]?.eqy_split_dt)
        .map((obj, i) => obj).length,
    );
    setAfterSplit(
      afterSplitDt
        .filter((date) => date === data[i]?.eqy_split_dt)
        .map((obj, i) => obj).length,
    );
  }, [data[i]?.eqy_split_dt, beforeSplitDt, afterSplitDt]);

  const useYesterday = closingDate === updatedDate || isWeekend ? false : true;

  if (!data[i] || signalAlertsSubscription === -1) return <div />;

  if (cell.column.Header === "Asset Class") {
    if (i > 0) {
      if (page[i - 1].cells[index].value === page[i].cells[index].value)
        return "";
    }
  }

  //POSITION - 'Closing Price' column
  if (cell.column.Header === "Closing Price") {
    if (useYesterday && !cell.row.original.daily) {
      return cell.value
        ? cell.value >= 10000
          ? noDecimals(cell.value.replace("$", ""))
          : cell.value.replace("$", "")
        : null;
    } else {
      return cell.row.original.px_last >= 10000
        ? noDecimals(cell.row.original.px_last.replace("$", ""))
        : cell.row.original.px_last
        ? cell.row.original.px_last.replace("$", "")
        : null;
    }
  }
  //POSITION - 'Last Price' column
  if (cell.column.Header === "Last Price") {
    if (isWeekend || !cell.row.original.daily) {
      return cell.value
        ? cell.value >= 10000
          ? noDecimals(cell.value.replace("$", ""))
          : cell.value.replace("$", "")
        : null;
    } else {
      return (
        <div
          data-tip={`This is not an intraday ticker, so the last price will not be available until market close at 04:30 pm EST.`}
          style={{ color: "#666666" }}
        >
          - - -
          {/* <div className='info-icon' style={{fontWeight: 100,color: "#bbb8b8",borderColor: "#707070",display: 'inline-block', marginLeft: 2}}>i</div> */}
        </div>
      );
    }
  }
  //POSITION - 'Description' column
  if (cell.column.Header === "Description") {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        <AlertModal
          setRefreshTable={setRefreshTable}
          cell={cell}
          ticker={data[i].ticker}
          id={data[i].id}
          planInterval={planInterval}
          alertsInterval={alertsInterval}
          // closingDate={closingDate}
          // updatedDate={updatedDate}
          setDisableDeleteContextMenu={setDisableDeleteContextMenu}
        />
        {!window.matchMedia("(max-width: 480px)").matches ? (
          <TickerModal
            cell={cell}
            id={data[i].id}
            split={data[i]?.eqy_split_dt}
            ratio={data[i].eqy_split_ratio}
            beforeSplit={beforeSplit}
            afterSplit={afterSplit}
            longbowPanel={longbowPanel}
            setRefreshTable={setRefreshTable}
            planInterval={planInterval}
            setPlanInterval={setPlanInterval}
            closingDate={closingDate}
            updatedDate={updatedDate}
            setDisableDeleteContextMenu={setDisableDeleteContextMenu}
            compactMode={compactMode}
            bullseye={bullseye}
          />
        ) : (
          <HoverModal
            cell={cell}
            id={data[i].id}
            split={data[i]?.eqy_split_dt}
            ratio={data[i].eqy_split_ratio}
            beforeSplit={beforeSplit}
            afterSplit={afterSplit}
            planInterval={planInterval}
            closingDate={closingDate}
            updatedDate={updatedDate}
            setDisableDeleteContextMenu={setDisableDeleteContextMenu}
          />
        )}
        {beforeSplit > 0 || afterSplit > 0 ? (
          <span>
            <span
              style={{
                border: ".5px solid #75ce41",
                borderRadius: "45%",
                padding: "1px 3.5px",
                fontSize: "6px",
                backgroundColor: "#222222",
                color: "#75ce41",
                marginLeft: 10,
                position: "relative",
                bottom: 3,
              }}
            >
              <i class="fa fa-dollar-sign"></i>
            </span>
            <span
              data-tip={`${data[i].ticker} 
                ${beforeSplit > 0 ? "will" : ""} split on 
                ${data[i].eqy_split_ratio} on 
                ${new Intl.DateTimeFormat("en-US", {
                  timeZone: "UTC",
                  year: "numeric",
                  month: "long",
                  day: "2-digit",
                }).format(new Date(data[i] ? data[i]?.eqy_split_dt : null))}.`}
              style={{
                border: ".5px solid #75ce41",
                borderRadius: "45%",
                padding: "1px 3.5px",
                fontSize: "6px",
                backgroundColor: "#222222",
                color: "#75ce41",
                position: "relative",
                top: 1,
                right: 5,
              }}
            >
              <i class="fa fa-dollar-sign"></i>
            </span>
          </span>
        ) : null}
      </div>
    );
  } //POSITION - 'Ticker' column
  else if (cell.column.Key === "bullseye_alert") {
    return (
      <div
        style={{
          cursor: "pointer",
          filter: (
            cell.row.original.alerts?.map(({ ticker }) => ticker) ?? []
          ).includes(cell.row.original.ticker)
            ? ""
            : "grayscale(1)",
        }}
        onClick={async () => {
          console.log(`mutation {
            update_panel(where: {name: {_eq: "Bullseye AI Trade Ideas"}}, _set: {alerts: ${JSON.stringify(
              (
                cell.row.original.alerts?.map((obj) => obj.ticker) ?? []
              ).includes(cell.row.original.ticker)
                ? cell.row.original.alerts.filter(
                    (obj) => obj.ticker !== cell.row.original.ticker,
                  )
                : [
                    ...cell.row.original.alerts,
                    { ticker: cell.row.original.ticker, timestamp: new Date() },
                  ],
            )}}) {
              affected_rows
            }
          }`);
          await API.graphql({
            query: `mutation($alerts: jsonb) {
            update_panel(where: {name: {_eq: "Bullseye AI Trade Ideas"}}, _set: {alerts: $alerts}) {
              affected_rows
            }
          }`,
            variables: {
              alerts: (
                cell.row.original.alerts?.map((obj) => obj.ticker) ?? []
              ).includes(cell.row.original.ticker)
                ? cell.row.original.alerts.filter(
                    (obj) => obj.ticker !== cell.row.original.ticker,
                  )
                : [
                    ...cell.row.original.alerts,
                    { ticker: cell.row.original.ticker, timestamp: new Date() },
                  ],
            },
          });
          await queryClient.refetchQueries(["bullseyeAlerts"]);
        }}
      >
        🚨
      </div>
    );
  } else if (cell.column.Header === "Ticker") {
    if (kiss)
      //On KISS View, show Kiss View Modal when clicking on the 'Ticker' column.
      return (
        <div>
          <HoverModal
            cell={cell}
            id={data[i].id}
            split={data[i]?.eqy_split_dt}
            ratio={data[i].eqy_split_ratio}
            beforeSplit={beforeSplit}
            afterSplit={afterSplit}
            planInterval={planInterval}
            closingDate={closingDate}
            updatedDate={updatedDate}
            setDisableDeleteContextMenu={setDisableDeleteContextMenu}
          />
          {/* If no Range Signal, show error message. */}
          {!cell.row.original.algo.data.CALC_TRADE_RANGE_PERCENT_LOW ? (
            <div
              data-tip={`Data is being processed and not currently available. Please check back later.`}
              className="info-icon"
              style={{
                fontWeight: 100,
                position: "absolute",
                top: "18px",
                right: "48px",
                zIndex: 4,
                color: "red",
              }}
            >
              !
            </div>
          ) : (
            <span></span>
          )}
        </div>
      );
    //Else, show the regular Ticker Modal.
    else
      return (
        <div>
          {!window.matchMedia("(max-width: 480px)").matches ? (
            <TickerModal
              cell={cell}
              id={data[i].id}
              split={data[i]?.eqy_split_dt}
              ratio={data[i].eqy_split_ratio}
              beforeSplit={beforeSplit}
              afterSplit={afterSplit}
              longbowPanel={longbowPanel}
              setRefreshTable={setRefreshTable}
              planInterval={planInterval}
              setPlanInterval={setPlanInterval}
              closingDate={closingDate}
              updatedDate={updatedDate}
              setDisableDeleteContextMenu={setDisableDeleteContextMenu}
              compactMode={compactMode}
              bullseye={bullseye}
            />
          ) : (
            <HoverModal
              cell={cell}
              id={data[i].id}
              split={data[i]?.eqy_split_dt}
              ratio={data[i].eqy_split_ratio}
              beforeSplit={beforeSplit}
              afterSplit={afterSplit}
              planInterval={planInterval}
              closingDate={closingDate}
              updatedDate={updatedDate}
              setDisableDeleteContextMenu={setDisableDeleteContextMenu}
            />
          )}
          {/* If no Range Signal, show error message. */}
          {!cell.row.original.algo.data.CALC_TRADE_RANGE_PERCENT_LOW ? (
            <div
              data-tip={`Data is being processed and is not currently available. Please check back later.`}
              className="info-icon"
              style={{
                fontWeight: 100,
                position: "absolute",
                top: "18px",
                right: "45px",
                zIndex: 4,
                color: "red",
              }}
            >
              !
            </div>
          ) : (
            <span></span>
          )}
        </div>
      );
  } //LONGBOW CORE SIGNAL - 'Set Alerts'
  else if (cell.column.Header === "Set Alerts") {
    //Show 'Set Alerts' column if subscribed to 'Signal Alerts' or include it by default from having the 'Master Plan'.
    if (signalAlertsSubscription || includeSignal)
      return (
        cell && (
          <AlertModal
            setRefreshTable={setRefreshTable}
            cell={cell}
            ticker={data[i].ticker}
            id={data[i].id}
            planInterval={planInterval}
            alertsInterval={alertsInterval}
          />
        )
      );
    //Or show dashes and upsell product.
    return <div style={{ margin: "auto 15px" }}>- - -</div>;
  } //LONGBOW CORE SIGNAL - Signal Strength Bars
  else if (cell.column.Header === "Signal Strength") {
    let signalStrength = 0;
    if (cell.row.original.algo.data.CALC_POSITION_LONGBOW_SIGNAL_STRENGTH) {
      signalStrength = Math.round(
        cell.row.original.algo.data.CALC_POSITION_LONGBOW_SIGNAL_STRENGTH * 10,
      );
      if (signalStrength >= 10) {
        signalStrength = 10;
      }
    }
    const signalColor = (index) => {
      let primaryColor = "#ffffff";
      let halfColor = "linear-gradient(#3E3E3E 50%, #ffffff 50%)";
      let neutralColor = "#3E3E3E";

      if (cell.row.original.algo.data.CALC_POSITION_LONGBOW_SIGNAL === "Buy") {
        primaryColor = "#75CE41";
        halfColor = "linear-gradient(#3E3E3E 50%, #75CE41 50%)";
        neutralColor = "#3E3E3E";
      }
      if (cell.row.original.algo.data.CALC_POSITION_LONGBOW_SIGNAL === "Sell") {
        primaryColor = "#D33541";
        halfColor = "linear-gradient(#57262B 50%, #D33541 50%)";
        neutralColor = "#57262B";
      }
      const style = [
        [neutralColor, neutralColor, neutralColor, neutralColor, neutralColor],
        [halfColor, neutralColor, neutralColor, neutralColor, neutralColor],
        [primaryColor, neutralColor, neutralColor, neutralColor, neutralColor],
        [primaryColor, halfColor, neutralColor, neutralColor, neutralColor],
        [primaryColor, primaryColor, neutralColor, neutralColor, neutralColor],
        [primaryColor, primaryColor, halfColor, neutralColor, neutralColor],
        [primaryColor, primaryColor, primaryColor, neutralColor, neutralColor],
        [primaryColor, primaryColor, primaryColor, halfColor, neutralColor],
        [primaryColor, primaryColor, primaryColor, primaryColor, neutralColor],
        [primaryColor, primaryColor, primaryColor, primaryColor, halfColor],
        [primaryColor, primaryColor, primaryColor, primaryColor, primaryColor],
      ][signalStrength];
      return style[index];
    };
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-end",
          justifyContent: "space-around",
          width: 40,
          marginRight: 10,
        }}
      >
        <div
          style={{
            height: compactMode ? 5 : 10,
            width: compactMode ? 4 : 5,
            backgroundImage: signalColor(0).startsWith("l")
              ? signalColor(0)
              : "unset",
            backgroundColor:
              (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !==
                -1) &
              (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) !==
                -1)
                ? signalColor(0)
                : "#3e3e3e",
            borderRadius: 20,
          }}
        ></div>
        <div
          style={{
            height: compactMode ? 10 : 15,
            width: compactMode ? 4 : 5,
            backgroundImage: signalColor(1).startsWith("l")
              ? signalColor(1)
              : "unset",
            backgroundColor:
              (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !==
                -1) &
              (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) !==
                -1)
                ? signalColor(1)
                : "#3e3e3e",
            borderRadius: 20,
          }}
        ></div>
        <div
          style={{
            height: compactMode ? 15 : 20,
            width: compactMode ? 4 : 5,
            backgroundImage: signalColor(2).startsWith("l")
              ? signalColor(2)
              : "unset",
            backgroundColor:
              (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !==
                -1) &
              (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) !==
                -1)
                ? signalColor(2)
                : "#3e3e3e",
            borderRadius: 20,
          }}
        ></div>
        <div
          style={{
            height: compactMode ? 20 : 25,
            width: compactMode ? 4 : 5,
            backgroundImage: signalColor(3).startsWith("l")
              ? signalColor(3)
              : "unset",
            backgroundColor:
              (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !==
                -1) &
              (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) !==
                -1)
                ? signalColor(3)
                : "#3e3e3e",
            borderRadius: 20,
          }}
        ></div>
        <div
          style={{
            height: compactMode ? 25 : 30,
            width: compactMode ? 4 : 5,
            backgroundImage: signalColor(4).startsWith("l")
              ? signalColor(4)
              : "unset",
            backgroundColor:
              (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !==
                -1) &
              (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) !==
                -1)
                ? signalColor(4)
                : "#3e3e3e",
            borderRadius: 20,
          }}
        ></div>
      </div>
    );
  } //LONGBOW CORE SIGNAL - 'Signal Pills'
  else if (cell.column.Header === "Signal") {
    return cell.row.original.algo.data ? (
      //If the trading range low/high values return a negative number, show dashes.
      Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) === -1 ||
      Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) === -1 ? (
        <div className="grey empty-value">- - -</div>
      ) : cell.row.original.algo.data.CALC_POSITION_LONGBOW_SIGNAL === "Buy" ? (
        <div className="green">Long</div>
      ) : cell.row.original.algo.data.CALC_POSITION_LONGBOW_SIGNAL ===
        "Sell" ? (
        <div className="red">Short</div>
      ) : cell.row.original.algo.data.CALC_POSITION_LONGBOW_SIGNAL ===
          "No Action" ||
        cell.row.original.algo.data.CALC_POSITION_LONGBOW_SIGNAL === "Hold" ? (
        <div className="grey">Neutral</div>
      ) : (
        <div className="grey empty-value">- - -</div>
      )
    ) : (
      (<div />)(<div />)
    );
  } //TRADING RANGE - Low
  else if (cell.column.Header === "Low") {
    return (
      <div
        style={
          cell.row.original.algo.data.CALC_TRADE_RANGE_LOW && {
            color: "#75ce41",
          }
        }
      >
        {/* not using numberFormat() because function breaks with prices above 100k ex: Naver Corp */}
        {cell.row.original.algo.data.CALC_TRADE_RANGE_LOW &&
        Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !== -1
          ? cell.row.original.algo.data.CALC_TRADE_RANGE_LOW >= 10000
            ? noDecimals(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW)
            : usCurrency(
                cell.row.original.algo.data.CALC_TRADE_RANGE_LOW,
                cell.row.original,
              )
          : "- - -"}
      </div>
    );
  } //TRADING RANGE - High
  else if (cell.column.Header === "High") {
    return (
      <div
        style={
          cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH && {
            color: "#d33541",
          }
        }
      >
        {/* not using numberFormat() because function breaks with prices above 100k ex: Naver Corp */}
        {cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH &&
        (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !== -1) &
          (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HiGH) !== -1)
          ? cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH >= 10000
            ? noDecimals(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH)
            : usCurrency(
                cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH,
                cell.row.original,
              )
          : "- - -"}
      </div>
    );
  } //TRADING RANGE -  Downside
  else if (cell.column.Header === "% from Trade Range | Low") {
    return (
      //If the trading range low/high values return a negative number, show dashes.
      <div
        style={
          (cell.row.original.algo.data.CALC_TRADE_RANGE_LOW != null) &
          (cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH != null) &
          (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) !== -1) &
          (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !== -1)
            ? parseFloat(
                cell.row.original.algo.data.CALC_TRADE_RANGE_PERCENT_LOW,
              ) < 0
              ? { color: "#d33541" }
              : parseFloat(
                  cell.row.original.algo.data.CALC_TRADE_RANGE_PERCENT_LOW,
                ) > 0
              ? { color: "#ffffff" }
              : { color: "#666666" }
            : { color: "#666666" }
        }
      >
        {(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW != null) &
        (cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH != null) &
        (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) !== -1) &
        (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !== -1)
          ? cell.row.original.algo.data.CALC_TRADE_RANGE_PERCENT_LOW
            ? cell.row.original.algo.data.CALC_TRADE_RANGE_PERCENT_LOW
            : "- - -"
          : "- - -"}
      </div>
    );
  } //TRADING RANGE -  Upside
  else if (cell.column.Header === "% from Trade Range | High") {
    return (
      //If the trading range low/high values return a negative number, show dashes.
      <div
        style={
          (cell.row.original.algo.data.CALC_TRADE_RANGE_LOW != null) &
          (cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH != null) &
          (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) !== -1) &
          (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !== -1)
            ? parseFloat(
                cell.row.original.algo.data.CALC_TRADE_RANGE_PERCENT_HIGH,
              ) < 0
              ? { color: "#ffffff" }
              : parseFloat(
                  cell.row.original.algo.data.CALC_TRADE_RANGE_PERCENT_HIGH,
                ) > 0
              ? { color: "#75CE41" }
              : { color: "#666666" }
            : { color: "#666666" }
        }
      >
        {(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW != null) &
        (cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH != null) &
        (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !== -1) &
        (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) !== -1)
          ? cell.row.original.algo.data.CALC_TRADE_RANGE_PERCENT_HIGH
            ? cell.row.original.algo.data.CALC_TRADE_RANGE_PERCENT_HIGH
            : "- - -"
          : "- - -"}
      </div>
    );
  } //TRADING RANGE -  Odds
  else if (cell.column.Header === "Odds") {
    return (
      //If the trading range low/high values return a negative number, show dashes.
      <div
        className="percentFrom"
        style={{
          display: "flex",
          color:
            (cell.row.original.algo.data.CALC_TRADE_RANGE_LOW != null) &
            (cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH != null) &
            (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !==
              -1) &
            (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) !== -1)
              ? cell.row.original.algo.data.CALC_TRADE_RANGE_ODDS < 0
                ? "#d33541"
                : cell.row.original.algo.data.CALC_TRADE_RANGE_ODDS > 0
                ? "#75ce41"
                : "#666666"
              : "#666666",
        }}
      >
        <div>
          {(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW != null) &
          (cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH != null) &
          (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !== -1) &
          (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) !== -1)
            ? cell.row.original.algo.data.CALC_TRADE_RANGE_ODDS
              ? cell.row.original.algo.data.CALC_TRADE_RANGE_ODDS.toFixed(1)
              : "- - -"
            : "- - -"}
        </div>
        {(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW != null) &
        (cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH != null) &
        (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !== -1) &
        (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) !== -1) ? (
          cell.row.original.algo.data.CALC_TRADE_RANGE_ODDS < 0 ? (
            <i
              className="fas fa-caret-down"
              style={{ position: "relative", left: 2, top: -3, fontSize: 20.5 }}
            />
          ) : cell.row.original.algo.data.CALC_TRADE_RANGE_ODDS > 0 ? (
            <i
              className="fas fa-caret-up"
              style={{ position: "relative", left: 2, top: -2, fontSize: 20 }}
            />
          ) : null
        ) : null}
      </div>
    );
  } //TRADING RANGE - Range
  else if (cell.column.Header === "Range") {
    if (cell.row.original.algo.data.CALC_TRADE_RANGE_PERCENT_LOW) {
      let low = 0;
      let high = 0;
      let left = 0;
      low =
        ((parseFloat(cell.row.original.px_last.replace(/,/g, "")) -
          parseFloat(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW)) /
          parseFloat(cell.row.original.px_last.replace(/,/g, ""))) *
        -100;
      high =
        ((parseFloat(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) -
          parseFloat(cell.row.original.px_last.replace(/,/g, ""))) /
          parseFloat(cell.row.original.px_last.replace(/,/g, ""))) *
        100;
      left = ((low * -1) / (low * -1 + high)) * 100;
      if (left < -34) left = -34;
      if (left > 129) left = 129;

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            height: compactMode ? 25 : 30,
            width: "100%",
          }}
        >
          <div
            style={{
              width: 1.5,
              height: "100%",
              backgroundColor: "#75ce41",
              position: "relative",
              right: 2,
            }}
          />
          <div
            style={{
              width: 2,
              height: "75%",
              backgroundColor: "#ffffff",
              position: "relative",
              left: left - 47 + "%",
            }}
          />
          <div
            style={{
              width: 1.5,
              height: "100%",
              backgroundColor: "#d33541",
              position: "relative",
              left: 2,
            }}
          />
        </div>
      );
    } else {
      return <span style={{ color: "#666666" }}>- - -</span>;
    }
  } //MOMENTUM INDICATOR - Short Term Signal
  else if (cell.column.Header === "S/T Momentum Signal") {
    const signalKey = "30D_ST";
    const signal = cell.row.original.algo.data[`${signalKey}_MOMENTUM`];
    const pxLast = parseFloat(cell.row.original.px_last);
    const pxClose = parseFloat(cell.row.original.px_close_1d);
    const pxSignal = parseFloat(
      cell.row.original.algo.data[`${signalKey}_PRICE`],
    );
    const isBroken = ["BEARISH", "BULLISH", "NEUTRAL"].includes(signal)
      ? pxClose < pxSignal
        ? pxLast > pxSignal
        : pxLast < pxSignal
      : false;

    return cell.row.original.algo.data ? (
      isBroken ? (
        <div style={{ position: "relative" }}>
          <img
            style={{ width: 69 }}
            src={`/broken_${signal.toLowerCase()}.svg`}
          />
          <span
            style={{
              textTransform: "capitalize",
              position: "absolute",
              left: 0,
              top: 3,
              width: "100%",
            }}
          >
            {signal.toLowerCase()}
          </span>
        </div>
      ) : Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) === -1 ||
        Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) === -1 ? (
        <div className="grey empty-value">- - -</div>
      ) : cell.row.original.algo.data["30D_ST_MOMENTUM"] === "BULLISH" ? (
        <div className="green">Bullish</div>
      ) : cell.row.original.algo.data["30D_ST_MOMENTUM"] === "BEARISH" ? (
        <div className="red">Bearish</div>
      ) : cell.row.original.algo.data["30D_ST_MOMENTUM"] === "NEUTRAL" ? (
        <div className="grey">Neutral</div>
      ) : (
        <div className="grey empty-value">- - -</div>
      )
    ) : (
      <div />
    );
  } //MOMENTUM INDICATOR - Short Term Price
  else if (cell.column.Header === "S/T Momentum Price") {
    //If the trading range low/high values return a negative number, show dashes.
    return (
      <div
        style={
          cell.row.original.algo.data["30D_ST_PRICE"] ||
          (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !== -1) &
            (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) !== -1)
            ? { color: "white" }
            : { color: "#666666" }
        }
      >
        {(Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !== -1) &
        (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) !== -1)
          ? cell.row.original.algo.data["30D_ST_PRICE"] ||
            cell.row.original.algo.data["30D_MT_PRICE"] > 0
            ? cell.row.original.algo.data["30D_ST_PRICE"] >= 10000
              ? noDecimals(
                  Math.abs(cell.row.original.algo.data["30D_ST_PRICE"]),
                )
              : usdCurrency(
                  Math.abs(cell.row.original.algo.data["30D_ST_PRICE"]),
                )
            : "- - -"
          : "- - -"}
      </div>
    );
  } //MOMENTUM INDICATOR - Medium Term Signal
  else if (cell.column.Header === "M/T Momentum Signal") {
    const signalKey = "90D_MT";
    const signal = cell.row.original.algo.data[`${signalKey}_MOMENTUM`];
    const pxLast = parseFloat(cell.row.original.px_last);
    const pxClose = parseFloat(cell.row.original.px_close_1d);
    const pxSignal = parseFloat(
      cell.row.original.algo.data[`${signalKey}_PRICE`],
    );
    const isBroken = ["BEARISH", "BULLISH", "NEUTRAL"].includes(signal)
      ? pxClose < pxSignal
        ? pxLast > pxSignal
        : pxLast < pxSignal
      : false;

    return cell.row.original.algo.data ? (
      isBroken ? (
        <div style={{ position: "relative" }}>
          <img
            style={{ width: 69 }}
            src={`/broken_${signal.toLowerCase()}.svg`}
          />
          <span
            style={{
              textTransform: "capitalize",
              position: "absolute",
              left: 0,
              top: 3,
              width: "100%",
            }}
          >
            {signal.toLowerCase()}
          </span>
        </div>
      ) : Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) === -1 ||
        Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) === -1 ? (
        <div className="grey empty-value">- - -</div>
      ) : cell.row.original.algo.data["90D_MT_MOMENTUM"] === "BULLISH" ? (
        <div className="green">Bullish</div>
      ) : cell.row.original.algo.data["90D_MT_MOMENTUM"] === "BEARISH" ? (
        <div className="red">Bearish</div>
      ) : cell.row.original.algo.data["90D_MT_MOMENTUM"] === "NEUTRAL" ? (
        <div className="grey">Neutral</div>
      ) : (
        <div className="grey empty-value">- - -</div>
      )
    ) : (
      <div />
    );
  } //MOMENTUM INDICATOR - Medium Term Price
  else if (cell.column.Header === "M/T Momentum Price") {
    //If the trading range low/high values return a negative number, show dashes.
    return (
      <div
        style={
          cell.row.original.algo.data["90D_MT_PRICE"] ||
          (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !== -1) &
            (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) !== -1)
            ? { color: "white" }
            : { color: "#666666" }
        }
      >
        {(Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !== -1) &
        (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) !== -1)
          ? cell.row.original.algo.data["90D_MT_PRICE"] ||
            cell.row.original.algo.data["90D_MT_PRICE"] > 0
            ? cell.row.original.algo.data["90D_MT_PRICE"] >= 10000
              ? noDecimals(
                  Math.abs(cell.row.original.algo.data["90D_MT_PRICE"]),
                )
              : usdCurrency(
                  Math.abs(cell.row.original.algo.data["90D_MT_PRICE"]),
                )
            : "- - -"
          : "- - -"}
      </div>
    );
  } //VOLATILITY - Implied Volatility (Discount) Premium Signal
  else if (cell.column.Header === "Premium | Signal") {
    return cell.row.original.algo.data ? (
      cell.row.original.algo.data["IVOL_SIGNAL"] === "Greed" ? (
        <div className="red">Discount</div>
      ) : cell.row.original.algo.data["IVOL_SIGNAL"] === "Fear" ? (
        <div className="green">Premium</div>
      ) : cell.row.original.algo.data["IVOL_SIGNAL"] === "Neutral" ? (
        <div className="grey">Neutral</div>
      ) : cell.row.original.algo.data["IVOL_SIGNAL"] === "NA" ? (
        <div className="grey empty-value">- - -</div>
      ) : (
        <div className="grey empty-value">- - -</div>
      )
    ) : (
      <div />
    );
  } //VOLATILITY - Implied Volatility (Discount) Premium Current/1D
  else if (cell.column.Header === "Premium | Current") {
    return cell.row.original.algo.data["CurrImpVolDiscPrem"] ? (
      <div
        style={{
          color:
            cell.row.original.algo.data["CurrImpVolDiscPrem"] * 100 > 0
              ? "#5ec72e"
              : "#D33541",
        }}
      >
        {parseInt(cell.row.original.algo.data["CurrImpVolDiscPrem"] * 100)}%
      </div>
    ) : (
      <div className="empty-value">- - -</div>
    );
  } //VOLATILITY - Implied Volatility (Discount) Premium 1W
  else if (cell.column.Header === "Premium | 1W") {
    return cell.row.original.algo.data["CurrImpVolDiscPrem7D"] ? (
      <div
        style={{
          color:
            cell.row.original.algo.data["CurrImpVolDiscPrem7D"] * 100 > 0
              ? "#5ec72e"
              : "#D33541",
        }}
      >
        {parseInt(cell.row.original.algo.data["CurrImpVolDiscPrem7D"] * 100)}%
      </div>
    ) : (
      <div className="empty-value">- - -</div>
    );
  } //VOLATILITY - Implied Volatility (Discount) Premium 1M
  else if (cell.column.Header === "Premium | ST <30D") {
    return cell.row.original.algo.data["CurrImpVolDiscPrem30D"] ? (
      <div
        style={{
          color:
            cell.row.original.algo.data["CurrImpVolDiscPrem30D"] * 100 > 0
              ? "#5ec72e"
              : "#D33541",
        }}
      >
        {parseInt(cell.row.original.algo.data["CurrImpVolDiscPrem30D"] * 100)}%
      </div>
    ) : (
      <div className="empty-value">- - -</div>
    );
  } //VOLATILITY - Implied Volatility (Discount) Premium 3M
  else if (cell.column.Header === "Premium | MT >90D") {
    return cell.row.original.algo.data["CurrImpVolDiscPrem90D"] ? (
      <div
        style={{
          color:
            cell.row.original.algo.data["CurrImpVolDiscPrem90D"] * 100 > 0
              ? "#5ec72e"
              : "#D33541",
        }}
      >
        {parseInt(cell.row.original.algo.data["CurrImpVolDiscPrem90D"] * 100)}%
      </div>
    ) : (
      <div className="empty-value">- - -</div>
    );
  } //VOLATILITY - Realized Volatility Current/1D
  else if (cell.column.Header === "Realized | Current") {
    return (
      <div
        style={
          !cell.value || cell.value === undefined ? { color: "#666666" } : null
        }
      >
        {cell.value ? parseInt(cell.value) + "%" : "- - -"}
      </div>
    );
  } //VOLATILITY - Realized Volatility 1W, 1M & 3M
  else if (
    cell.column.Header === "Realized | 1W" ||
    cell.column.Header === "Realized | 1M" ||
    cell.column.Header === "Realized | 3M"
  ) {
    return (
      <div
        style={
          !cell.value || cell.value === undefined ? { color: "#666666" } : null
        }
      >
        {cell.value ? parseInt(cell.value) + "%" : "- - -"}
      </div>
    );
  } //TRADING RANGE - Range Signal &
  //[KISS VIEW] LONGBOW TRADING RANGE DYNAMICS - Range Signal
  else if (cell.column.Header === "Range Signal") {
    return (
      <span
        className={
          (cell.row.original.algo.data.CALC_TRADE_RANGE_LOW != null) &
          (cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH != null) &
          (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !== -1) &
          (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) !== -1)
            ? cell.row.original.algo.data["CALC_TRADING_RANGE_SIGNAL"] === "Buy"
              ? "green"
              : cell.row.original.algo.data["CALC_TRADING_RANGE_SIGNAL"] ===
                "Sell"
              ? "red"
              : cell.row.original.algo.data["CALC_TRADING_RANGE_SIGNAL"] ===
                "Hold"
              ? "grey"
              : "grey empty-value"
            : "grey empty-value"
        }
      >
        {(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW != null) &
        (cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH != null) &
        (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_LOW) !== -1) &
        (Math.max(cell.row.original.algo.data.CALC_TRADE_RANGE_HIGH) !== -1)
          ? cell.row.original.algo.data["CALC_TRADING_RANGE_SIGNAL"]
            ? cell.row.original.algo.data["CALC_TRADING_RANGE_SIGNAL"] === "Buy"
              ? "Oversold"
              : cell.row.original.algo.data["CALC_TRADING_RANGE_SIGNAL"] ===
                "Sell"
              ? "Overbought"
              : "Neutral"
            : "- - -"
          : "- - -"}
      </span>
    );
  } //[KISS VIEW] LONGBOW TRADING RANGE DYNAMICS - Asymmetry
  else if (cell.column.Key === "calc_kiss_asymmetry") {
    return cell.column.Key ? (
      <span
        className={
          cell.row.original.algo.data.CALC_TRADE_RANGE_ODDS > 0
            ? "green"
            : cell.row.original.algo.data.CALC_TRADE_RANGE_ODDS < 0
            ? "red"
            : cell.row.original.algo.data.CALC_TRADE_RANGE_ODDS === 0
            ? "grey"
            : "grey empty-value"
        }
      >
        {cell.row.original.algo.data.CALC_TRADE_RANGE_ODDS > 0
          ? "Bullish"
          : cell.row.original.algo.data.CALC_TRADE_RANGE_ODDS < 0
          ? "Bearish"
          : "- - -"}
      </span>
    ) : null;
  } //[KISS VIEW] LONGBOW TRADING RANGE DYNAMICS - Low End
  else if (cell.column.Key === "calc_kiss_low_end") {
    return cell.column.Key ? (
      <span
        className={
          cell.row.original.algo_daily_kiss_low === "Higher"
            ? "green"
            : cell.row.original.algo_daily_kiss_low === "Lower"
            ? "red"
            : cell.row.original.algo_daily_kiss_low === "Neutral"
            ? "grey"
            : "grey empty-value"
        }
      >
        {cell.row.original.algo_daily_kiss_low
          ? cell.row.original.algo_daily_kiss_low
          : "- - -"}
      </span>
    ) : null;
  } //[KISS VIEW] LONGBOW TRADING RANGE DYNAMICS - High End
  else if (cell.column.Key === "calc_kiss_high_end") {
    return cell.column.Key ? (
      <span
        className={
          cell.row.original.algo_daily_kiss_high === "Higher"
            ? "green"
            : cell.row.original.algo_daily_kiss_high === "Lower"
            ? "red"
            : cell.row.original.algo_daily_kiss_high === "Neutral"
            ? "grey"
            : "grey empty-value"
        }
      >
        {cell.row.original.algo_daily_kiss_high
          ? cell.row.original.algo_daily_kiss_high
          : "- - -"}
      </span>
    ) : null;
  } //[KISS VIEW] SHORT TERM SIGNALS - Short Term Momentum (S/T)
  else if (cell.column.Key === "calc_kiss_30d_momentum") {
    return cell.column.Key ? (
      <span
        className={
          cell.row.original.algo.data["30D_ST_MOMENTUM"] === "BULLISH"
            ? "green"
            : cell.row.original.algo.data["30D_ST_MOMENTUM"] === "BEARISH"
            ? "red"
            : cell.row.original.algo.data["30D_ST_MOMENTUM"] === "NEUTRAL"
            ? "grey"
            : "grey empty-value"
        }
      >
        {cell.row.original.algo.data["30D_ST_MOMENTUM"]
          ? cell.row.original.algo.data["30D_ST_MOMENTUM"][0] +
            cell.row.original.algo.data["30D_ST_MOMENTUM"]
              .slice(1)
              .toLowerCase()
          : "- - -"}
      </span>
    ) : null;
  } //[KISS VIEW] SHORT TERM SIGNALS - 3D Momentum
  else if (cell.column.Key === "calc_kiss_30d_3d_momentum") {
    return cell.column.Key ? (
      <span
        className={
          cell.row.original.algo.data["30D_ST_MOMENTUM_3D"] === "BULLISH"
            ? "green"
            : cell.row.original.algo.data["30D_ST_MOMENTUM_3D"] === "BEARISH"
            ? "red"
            : cell.row.original.algo.data["30D_ST_MOMENTUM_3D"] === "NEUTRAL"
            ? "grey"
            : "grey empty-value"
        }
      >
        {cell.row.original.algo.data["30D_ST_MOMENTUM_3D"]
          ? cell.row.original.algo.data["30D_ST_MOMENTUM_3D"][0] +
            cell.row.original.algo.data["30D_ST_MOMENTUM_3D"]
              .slice(1)
              .toLowerCase()
          : "- - -"}
      </span>
    ) : null;
  } //[KISS VIEW] SHORT TERM SIGNALS - Current IVOL Premium/Discount
  else if (cell.column.Key === "calc_kiss_30d_ivol_premium") {
    return cell.column.Key ? (
      <span
        className={
          cell.row.original.algo.data["IVOL_SIGNAL"] === "Greed"
            ? "red"
            : cell.row.original.algo.data["IVOL_SIGNAL"] === "Fear"
            ? "green"
            : cell.row.original.algo.data["IVOL_SIGNAL"] === "Neutral"
            ? "grey"
            : "grey empty-value"
        }
        style={{ padding: "0px 20px" }}
      >
        {cell.row.original.algo.data["IVOL_SIGNAL"] &&
        cell.row.original.algo.data["IVOL_SIGNAL"] !== "NA"
          ? cell.row.original.algo.data["IVOL_SIGNAL"] === "Greed"
            ? "Discount"
            : cell.row.original.algo.data["IVOL_SIGNAL"] === "Fear"
            ? "Premium"
            : "Neutral"
          : "- - -"}
      </span>
    ) : null;
  } //[KISS VIEW] SHORT TERM SIGNALS - 1W Ago IVOL Premium/Discount
  else if (cell.column.Key === "calc_kiss_90d_momentum") {
    return cell.column.Key ? (
      <span
        className={
          cell.row.original.algo.data["90D_MT_MOMENTUM"] === "BULLISH"
            ? "green"
            : cell.row.original.algo.data["90D_MT_MOMENTUM"] === "BEARISH"
            ? "red"
            : cell.row.original.algo.data["90D_MT_MOMENTUM"] === "NEUTRAL"
            ? "grey"
            : "grey empty-value"
        }
      >
        {cell.row.original.algo.data["90D_MT_MOMENTUM"] === "BULLISH"
          ? "Bullish"
          : cell.row.original.algo.data["90D_MT_MOMENTUM"] === "BEARISH"
          ? "Bearish"
          : cell.row.original.algo.data["90D_MT_MOMENTUM"] === "NEUTRAL"
          ? "Neutral"
          : "- - -"}
      </span>
    ) : null;
  } //[KISS VIEW] MEDIUM TERM SIGNALS - Medium Term Momentum
  else if (cell.column.Key === "calc_kiss_30d_1w_ivol_premium") {
    return cell.column.Key ? (
      <span
        className={
          cell.row.original.algo.data["IVOL_1WK_CHECK"] === "Bullish"
            ? "green"
            : cell.row.original.algo.data["IVOL_1WK_CHECK"] === "Bearish"
            ? "red"
            : cell.row.original.algo.data["IVOL_1WK_CHECK"] === "Neutral"
            ? "grey"
            : "grey empty-value"
        }
        style={{ padding: "0px 10px" }}
      >
        {cell.row.original.algo.data["IVOL_1WK_CHECK"] === "Bullish"
          ? "Premium"
          : cell.row.original.algo.data["IVOL_1WK_CHECK"] === "Bearish"
          ? "Discount"
          : cell.row.original.algo.data["IVOL_1WK_CHECK"] === "Neutral"
          ? "Neutral"
          : "- - -"}
      </span>
    ) : null;
  } //[KISS VIEW] MEDIUM TERM SIGNALS - 3M Ago IVOL Premium/Discount
  else if (cell.column.Key === "calc_kiss_90d_ivol_premium") {
    return cell.column.Key ? (
      <span
        className={
          cell.row.original.algo.data["IVOL_3MO_CHECK"] === "Bullish"
            ? "green"
            : cell.row.original.algo.data["IVOL_3MO_CHECK"] === "Bearish"
            ? "red"
            : cell.row.original.algo.data["IVOL_3MO_CHECK"] === "Neutral"
            ? "grey"
            : "grey empty-value"
        }
        style={{ padding: "0px 10px" }}
      >
        {cell.row.original.algo.data["IVOL_3MO_CHECK"] === "Bullish"
          ? "Premium"
          : cell.row.original.algo.data["IVOL_3MO_CHECK"] === "Bearish"
          ? "Discount"
          : cell.row.original.algo.data["IVOL_3MO_CHECK"] === "Neutral"
          ? "Neutral"
          : "- - -"}
      </span>
    ) : null;
  } else if (cell.column.Header === "SI Signal") {
    if (cell.row.original.si_percent_equity_float) {
      const si = parseFloat(
        cell.row.original.si_percent_equity_float.replace("%", ""),
      );
      return (
        <span
          className={
            si < 6
              ? "green"
              : si > 12
              ? "red"
              : si >= 6 && si <= 12
              ? "grey"
              : "grey empty-value"
          }
          style={{ padding: "0px 10px" }}
        >
          {si < 6
            ? "Low"
            : si > 12
            ? "High"
            : si >= 6 && si <= 12
            ? "Medium"
            : "- - -"}
        </span>
      );
    } else {
      return null;
    }
  } else if (cell.column.Key === "vip_win_pct") {
    return <MoneylineColumn cell={cell} moneylineKey="win_pct" />;
  } else if (cell.column.Key === "vip_avg_pct_gain") {
    return <MoneylineColumn cell={cell} moneylineKey="avg_pct_gain" />;
  } else if (cell.column.Key === "vip_loss_pct") {
    return <MoneylineColumn cell={cell} moneylineKey="loss_pct" />;
  } else if (cell.column.Key === "vip_avg_pct_loss") {
    return <MoneylineColumn cell={cell} moneylineKey="avg_pct_loss" />;
  } else if (!cell.value) {
    return <span style={{ color: "#666666" }}>- - -</span>;
  } //VOLATILITY - Beta
  else if (cell.column.Header === "Beta") {
    return <span style={{ color: "#ffffff" }}>{cell.value}</span>;
  } //% Change in # Shares Short - Vs.Last Period & M/M
  else if (
    ["Vs. Last Period / Date", "Mth-Over-Mth / Date"].includes(
      cell.column.Header,
    )
  ) {
    return (
      <span
        style={{
          color:
            parseFloat(cell.value) === 0
              ? "#ffffff"
              : parseFloat(cell.value) < 0
              ? "#d33541"
              : "#75ce41",
        }}
      >
        {cell.value}
      </span>
    );
  } //PRICE RETURNS & VOLUME
  else if (
    [
      "1-Day / % Chg",
      "5-Day / % Chg",
      "1-Mth / % Chg",
      "3-Mth / % Chg",
      "YTD / % Chg",
    ].includes(cell.column.Header)
  ) {
    return (
      <span
        style={{
          color:
            parseFloat(cell.value) === 0
              ? "#ffffff"
              : parseFloat(cell.value) < 0
              ? "#d33541"
              : "#75ce41",
        }}
      >
        {cell.value[cell.value.length - 1] !== "%" && cell.value[0] !== "$"
          ? Number(cell.value).toFixed(2) + "%"
          : cell.value}
      </span>
    );
  } //SHORT INTEREST - % Equity Float
  else if (cell.column.Header === "% Equity Float / Date")
    return <span style={{ color: "#ffffff" }}>{cell.value}</span>;
  else if (cell.column.Key === "long_short")
    return (
      <span
        className={
          cell.value === "Long"
            ? "green"
            : cell.value === "Short"
            ? "red"
            : "grey empty-value"
        }
        style={{ padding: "0px 10px" }}
      >
        {cell.value}
      </span>
    );
  else if (cell.column.Key === "exposure")
    return <span>{(cell.value * 100).toFixed(2) + "%"}</span>;
  else if (cell.column.Key === "entry_date")
    return <span style={{ color: "#ffffff" }}>{cell.value}</span>;
  else if (cell.column.Key === "entry_type")
    return (
      <div
        style={{ maxWidth: 50, marginRight: -3, marginLeft: -3 }}
        className={cell.value === "Buy" ? "green" : "red"}
      >
        {cell.value}
      </div>
    );
  else if (cell.column.Key === "entry_price")
    return (
      <span
        style={{
          color:
            parseFloat(cell.value) === parseFloat(cell.row.original.px_last)
              ? "#ffffff"
              : parseFloat(cell.value) < parseFloat(cell.row.original.px_last)
              ? "#d33541"
              : "#75ce41",
        }}
      >
        {usdCurrency(cell.value)}
      </span>
    );
  else if (cell.column.Key === "itd_pct_chg")
    return (
      <span
        style={{
          color:
            parseFloat(cell.value) === 0
              ? "#ffffff"
              : parseFloat(cell.value) < 0
              ? "#d33541"
              : "#75ce41",
        }}
      >
        {Number(cell.value).toFixed(2) + "%"}
      </span>
    );
  else if (cell.column.Key === "volume_avg_5d")
    return (
      <span>
        {Number(cell.value)?.toLocaleString() !== "NaN"
          ? Number(cell.value)?.toLocaleString()
          : "--"}
      </span>
    );

  return cell.value[cell.value.length - 1] !== "%" && cell.value[0] !== "$"
    ? Number(cell.value).toFixed(2) + "%"
    : cell.value;
}
<Tooltip />;

export default React.memo(FormatCell);
